import { AnimatePresence } from 'framer-motion';
import { AdminLogo, VDriveLogo, XModalIcon, successPNG } from '../../assets';
import {
  ModalHeader,
  ModalOverlay,
  ModalStyled,
  SuccessImage,
  ModalScrollableStyled,
  ModalFooter,
  Image,
  ModalOverflowStyled,
  ModalGlowBorder,
  ModalShimmer,
  ModalGlowWrapper,
} from './modal.styled';
import { Div, H3, Icon, P, lightTheme, modalVariants } from '../../styles';
import { Dispatch, SetStateAction, useId, useRef } from 'react';
import { useFocusTrap } from '../../hooks/useFocusTrap';

export const Modal = ({
  children,
  isOpen,
  closeModal,
  logo = true,
  vDriveLogo = false,
  exitButton = true,
  successImage,
  header,
  titleWithExit,
  titleWithTextComponent,
  width,
  footer,
  zIndex,
  textAlign,
  overflow,
  modalOverflow,
  minHeight,
  maxHeight,
  minHeightSM,
  maxHeightSM,
  image,
  $numOfFloatingButtons,
  subtitle,
  overlayBorderRadius = 0,
  backgroundColor,
  padding,
  glow,
}: ModalProps) => {
  // useDisableBodyScroll(isOpen);

  // a11y: focus trap + ESC + return-focus, matches ModalPortal.
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(dialogRef, { active: isOpen, onEscape: closeModal });

  const ariaLabel =
    typeof header === 'string'
      ? undefined
      : typeof titleWithExit === 'string'
      ? undefined
      : 'Dialog';
  const ariaLabelledBy = header || titleWithExit ? titleId : undefined;

  // Opting into `glow` at all (even `glow={false}`) mounts the wrapper for good
  // and only its visuals are toggled — swapping the wrapper in and out mid-flight
  // would remount the modal subtree and replay the open animation (a blink).
  const glowable = glow !== undefined;

  const motionProps = {
    variants: modalVariants,
    initial: 'initial',
    animate: 'animate',
    exit: 'exit',
  } as const;

  const box = (
    <ModalStyled
      key="modal"
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      tabIndex={-1}
      {...(glowable ? {} : motionProps)}
      $inWrapper={glowable}
      $width={width}
      $zIndex={zIndex}
      $textAlign={textAlign}
      $modalOverflow={modalOverflow}
      $backgroundColor={backgroundColor}
    >
      {header && (
        <ModalHeader>
          <H3 id={titleId} $m="0" $color={lightTheme.white}>
            {header}
          </H3>
          {subtitle && (
            <P $m="0" $color={lightTheme.white}>
              {subtitle}
            </P>
          )}
        </ModalHeader>
      )}

      {footer && <ModalFooter>{footer}</ModalFooter>}

      <ModalScrollableStyled
        $overflow={overflow}
        $minHeight={minHeight}
        $maxHeight={maxHeight}
        $minHeightSM={minHeightSM}
        $maxHeightSM={maxHeightSM}
      >
        <ModalOverflowStyled
          $logo={logo}
          $vDriveLogo={vDriveLogo}
          $successImage={successImage || !!image}
          $footer={!!footer}
          $numOfFloatingButtons={$numOfFloatingButtons}
          $overflow={overflow}
          $padding={padding}
        >
          {titleWithExit && (
            // With a subtitle under it the title block is two lines tall, and
            // centring the X against it left the X floating mid-height; it
            // belongs in the corner. A one-line title stays centred.
            <Div
              $flex
              $between
              $alignCenter={!subtitle}
              $alignStart={!!subtitle}
              $mb="24"
            >
              {/* `subtitle` renders here as well as under `header`, so the
                  white-header and title-with-exit variants read the same way.
                  Sits inside the title block rather than above `children` so
                  it stays tied to the heading instead of drifting down with
                  the modal's own content padding. `aria-labelledby` still
                  points at the H3 alone, so the subtitle does not end up in
                  the dialog's accessible name. */}
              <div>
                <H3 id={titleId} $m="0" $left>
                  {titleWithExit}
                </H3>
                {subtitle && (
                  <P $m="0" $left $color={lightTheme.lightGray}>
                    {subtitle}
                  </P>
                )}
              </div>
              <Div $flex $alignCenter $gap="16px">
                {titleWithTextComponent && titleWithTextComponent}
                <button
                  type="button"
                  aria-label="Close dialog"
                  onClick={() => closeModal?.()}
                  style={{
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon $pointer>
                    <svg
                      width="18"
                      height="19"
                      viewBox="0 0 18 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="redSvgHover"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M17.4716 3.6638C18.1747 2.94469 18.1747 1.77687 17.4716 1.05776C16.7684 0.338656 15.6266 0.338656 14.9234 1.05776L9.00031 7.12126L3.07156 1.06351C2.36844 0.344409 1.22656 0.344409 0.523437 1.06351C-0.179687 1.78262 -0.179687 2.95045 0.523437 3.66955L6.45219 9.72729L0.529062 15.7908C-0.174063 16.5099 -0.174063 17.6777 0.529062 18.3968C1.23219 19.1159 2.37406 19.1159 3.07719 18.3968L9.00031 12.3333L14.9291 18.3911C15.6322 19.1102 16.7741 19.1102 17.4772 18.3911C18.1803 17.672 18.1803 16.5041 17.4772 15.785L11.5484 9.72729L17.4716 3.6638Z"
                        fill={header ? lightTheme.white : lightTheme.lightGray}
                      />
                    </svg>
                  </Icon>
                </button>
              </Div>
            </Div>
          )}
          {children}
        </ModalOverflowStyled>
      </ModalScrollableStyled>
      {logo && <AdminLogo />}
      {vDriveLogo && <VDriveLogo width="160" height="160" />}
      {successImage && <SuccessImage src={successPNG} alt="success" />}
      {image && <Image src={image} alt="mage" />}

      {!titleWithExit && exitButton && (
        <button
          type="button"
          aria-label="Close dialog"
          onClick={() => closeModal?.()}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            cursor: 'pointer',
            background: 'transparent',
            border: 0,
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <XModalIcon
            color={header ? lightTheme.white : lightTheme.lightGray}
          />
        </button>
      )}

      {glowable && (
        <>
          <ModalShimmer $active={glow} />
          <ModalGlowBorder $active={glow} />
        </>
      )}
    </ModalStyled>
  );

  return (
    <AnimatePresence mode="wait">
      {isOpen &&
        (glowable ? (
          <ModalGlowWrapper
            key="modal"
            {...motionProps}
            $zIndex={zIndex}
            $width={width}
            $active={glow}
          >
            {box}
          </ModalGlowWrapper>
        ) : (
          box
        ))}
      {isOpen && (
        <ModalOverlay
          key="modalOverlay"
          aria-hidden="true"
          variants={modalVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={() => closeModal?.()}
          $zIndex={zIndex}
          style={{ borderRadius: overlayBorderRadius }}
        />
      )}
    </AnimatePresence>
  );
};

type ModalProps = {
  children: React.ReactNode;
  isOpen: boolean;
  closeModal?: () => void;
  logo?: boolean;
  vDriveLogo?: boolean;
  exitButton?: boolean;
  successImage?: boolean;
  header?: string;
  titleWithExit?: string;
  titleWithTextComponent?: React.ReactNode;
  width?: string;
  footer?: React.ReactNode;
  zIndex?: number;
  textAlign?: string;
  overflow?: string;
  modalOverflow?: string;
  /**
   * Height of the scrolling area, when a modal needs to stop resizing itself
   * around its content. Setting both to the same value fixes it, and the
   * modal's own scroller takes over from there. Left off, it grows with the
   * content up to 90vh as before.
   *
   * The SM pair overrides the other two below 800px, where a height that
   * suits a desktop dialog is usually most of a phone screen.
   */
  minHeight?: string;
  maxHeight?: string;
  minHeightSM?: string;
  maxHeightSM?: string;
  image?: string;
  $numOfFloatingButtons?: number;
  subtitle?: string;
  overlayBorderRadius?: number;
  backgroundColor?: string;
  padding?: string;
  glow?: boolean;
};

export type PropsModal = Omit<ModalProps, 'children' | 'isOpen'> & {
  setLoading?: Dispatch<SetStateAction<boolean>>;
};
